import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import StarRating from '@/components/shared/StarRating';
import { getCategoryDefinitions, getInsurerRating } from '@/features/Quote/types/insurerRating';
import { isFeatureActive } from '@/lib/featureFlags';
import { FeatureFlag } from '@/types/app';
import InsurerRatingDrawer from './InsurerRatingDrawer';

interface InsurerRatingProps {
	imageKey?: string;
	insurerName: string;
	className?: string;
}

const InsurerRating = ({ imageKey, insurerName, className }: InsurerRatingProps) => {
	const { t } = useTranslation();
	const { config } = useAppContext();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const ratingData = imageKey ? getInsurerRating(imageKey) : null;
	const categoryDefinitions = getCategoryDefinitions();

	// Check if vendor ratings feature is enabled
	const isVendorRatingsEnabled = isFeatureActive(config, FeatureFlag.ENABLE_PROVIDER_RATINGS, true);

	// Don't render if feature is disabled or no rating data exists for this insurer
	if (!isVendorRatingsEnabled || !ratingData) {
		return null;
	}

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsDrawerOpen(true);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			setIsDrawerOpen(true);
		}
	};

	return (
		<>
			<div
				className={className}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
				data-testid="insurer-rating"
				aria-label={t('quotes.rating.ariaLabel', {
					rating: ratingData.overallRating.toFixed(1),
					name: ratingData.name
				})}
			>
				<StarRating rating={ratingData.overallRating} size="sm" clickable />
			</div>

			<InsurerRatingDrawer
				open={isDrawerOpen}
				onOpenChange={setIsDrawerOpen}
				insurerName={insurerName}
				imageKey={imageKey}
				ratingData={ratingData}
				categoryDefinitions={categoryDefinitions}
			/>
		</>
	);
};

export default InsurerRating;
